import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { aiSearchQueryAPI } from '../../../../../util/api';

import css from './AIChatSearch.module.css';

const AIChatSearch = forwardRef(({ onSuggestionSelect, onLoadingChange, theme, className }, ref) => {
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Typing animation state
  const edgyPlaceholders = [
    'Ask AI: "fishing boat in Ottawa"',
    'Ask AI: "jet ski near Miami"',
    'Ask AI: "party yacht in Vancouver"',
    'Ask AI: "kayak rental in Muskoka"',
    'Ask AI: "pontoon in Kelowna"',
    'Ask AI: "diving tour in Cancun"',
    'Ask AI: "sailboat in Halifax"',
    'Ask AI: "seadoo in Toronto"',
  ];

  const [shuffledPlaceholders] = useState(() => {
    const shuffled = [...edgyPlaceholders];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isWaiting, setIsWaiting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const hasUserInput = query.length > 0;

  // Typing animation
  useEffect(() => {
    if (isInputFocused || hasUserInput) return;

    const currentPlaceholder = shuffledPlaceholders[currentIndex];
    let timeoutId;

    if (isTyping) {
      if (displayText.length < currentPlaceholder.length) {
        timeoutId = setTimeout(() => {
          setDisplayText(currentPlaceholder.slice(0, displayText.length + 1));
        }, 50 + Math.random() * 100);
      } else {
        setIsTyping(false);
        setIsWaiting(true);
        timeoutId = setTimeout(() => {
          setIsWaiting(false);
        }, 4000);
      }
    } else if (!isWaiting) {
      if (displayText.length > 0) {
        timeoutId = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 30);
      } else {
        setCurrentIndex(prev => (prev + 1) % shuffledPlaceholders.length);
        setIsTyping(true);
        timeoutId = setTimeout(() => {
          setIsTyping(true);
        }, 800);
      }
    }

    return () => clearTimeout(timeoutId);
  }, [displayText, isTyping, isWaiting, currentIndex, shuffledPlaceholders, isInputFocused, hasUserInput]);

  // Cursor blink
  useEffect(() => {
    if (isInputFocused || hasUserInput) {
      setShowCursor(false);
      return;
    }
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, [isInputFocused, hasUserInput]);

  // Reset animation when input becomes empty
  useEffect(() => {
    if (!isInputFocused && !hasUserInput && displayText === '') {
      setIsTyping(true);
      setIsWaiting(false);
    }
  }, [isInputFocused, hasUserInput, displayText]);

  const handleSubmit = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    onLoadingChange?.(true);
    try {
      const response = await aiSearchQueryAPI({ message: trimmed });
      if (response.success && response.suggestions?.length > 0) {
        // Navigate directly to search with the first suggestion's params
        onSuggestionSelect?.(response.suggestions[0].params);
      } else {
        // Fallback to keyword search
        onSuggestionSelect?.({ keywords: trimmed });
      }
    } catch (err) {
      console.error('AI search error:', err);
      // Fallback to keyword search
      onSuggestionSelect?.({ keywords: trimmed });
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [query, loading, onSuggestionSelect]);

  useImperativeHandle(ref, () => ({
    triggerSearch: () => handleSubmit(),
    hasQuery: () => query.trim().length > 0,
  }), [handleSubmit, query]);

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const placeholderWithCursor =
    isInputFocused || hasUserInput ? '' : displayText + (showCursor ? '|' : '');

  const isLight = theme === 'light';

  return (
    <div className={classNames(css.root, { [css.themeLight]: isLight }, className)}>
      <div className={css.inputWrapper}>
        <div className={css.customField}>
          <input
            ref={inputRef}
            className={css.input}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder={placeholderWithCursor}
            autoComplete="off"
            disabled={loading}
          />
        </div>
      </div>

    </div>
  );
});

export default AIChatSearch;
